import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import { connect } from "react-redux";

import { betaVsLiveSwitchToggle, betaVsLiveUpdateCompleted } from "app/actions/commonActions";
import { swSettings } from "app/common/services/swSettings";
import { EPageIdOption, SegmentsUpsaleBanner } from "app/pages/segments/upsale-banner/SegmentsUpsaleBanner";
import { TrafficCompareModeContainer } from "app/pages/website-analysis/TrafficAndEngagement/Components/Compare/TrafficCompareModeContainer";
import { TrafficSingleBetaModeContainer } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/TrafficSingleBetaModeContainer";
import { TrafficSingleModeContainer } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/TrafficSingleModeContainer";
import { TrafficSingleModePOPContainer } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/TrafficSinglePOPModeContainer";
import { USE_BETA_BRANCH_DATA_PREF_KEY } from "app/routes/websiteStateConfigConstants";
import { connectedAccountsServiceFactory } from "app/services/connectedAccounts.service";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { EMPTY_CUSTOM_SEGMENTS } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import WebsiteAnalysisApiService from "app/services/WebsiteAnalysis/WebsitAnalysisApiService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TrafficComparePOPModeContainer } from "./Components/trafficCompareModePopContainer/TrafficCompareModePopContainer";

const COMPONENTS = {
    single: TrafficSingleModeContainer,
    compare: TrafficCompareModeContainer,
    singlePOP: TrafficSingleModePOPContainer,
    comparePOP: TrafficComparePOPModeContainer,
    singleBetaBranch: TrafficSingleBetaModeContainer,
    compareBetaBranch: TrafficCompareModeContainer,
};

const WebsiteAnalysisTrafficContainer: React.FunctionComponent<any> = (props) => {
    const { showBetaBranchData, betaVsLiveSwitchToggle, betaVsLiveUpdateCompleted, customSegmentsMeta } = props;
    const { params } = useCurrentStateAndParams();
    const { duration, comparedDuration, country, key } = params;
    const doSegmentsExist = useMemo(
        () => SegmentsUtils.doSegmentsExistInAccount(customSegmentsMeta) || SegmentsUtils.doSegmentsExistInUserData(),
        [customSegmentsMeta],
    );
    const [displaySegmentsUpsaleBanner, setDisplaySegmentsUpsaleBanner] = useState(false);
    const [hasClosedSegmentsUpsaleBanner, setHasClosedSegmentsUpsaleBanner] = useState(false);
    const closeSegmentsUpsaleBanner = useCallback(() => {
        setDisplaySegmentsUpsaleBanner(false);
        setHasClosedSegmentsUpsaleBanner(true);
    }, []);
    const durationObject = React.useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [comparedDuration, duration],
    );
    useEffect(() => {
        if (
            showBetaBranchData &&
            (comparedDuration ||
                duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS ||
                durationObject.raw.from.isBefore(
                    dayjs(swSettings.components.TrafficAndEngagementBeta?.resources?.StartDate),
                ) ||
                !swSettings.components.TrafficAndEngagementBeta?.resources?.AllowedCountries.includes(
                    parseInt(country, 10),
                ))
        ) {
            betaVsLiveSwitchToggle(false, true);
            PreferencesService.add({
                [USE_BETA_BRANCH_DATA_PREF_KEY]: false,
            }).then(() => {
                betaVsLiveUpdateCompleted();
            });
        }
    }, [params]);

    const services = React.useMemo(
        () => ({
            swNavigator: proNavigator,
            websiteAnalysisApiService: new WebsiteAnalysisApiService(),
            swConnectedAccountsService: connectedAccountsServiceFactory(),
        }),
        [],
    );
    const apiParams = useMemo(() => {
        const responseParams = services.swNavigator.getApiParams(params);
        const { compareFrom, compareTo } = durationObject?.forAPI;
        return {
            ...responseParams,
            compareFrom,
            compareTo,
            duration,
        };
    }, [params, services.swNavigator, durationObject]);
    const isGAPrivate = useMemo(
        () => services.swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(apiParams.keys),
        [apiParams.keys, services.swConnectedAccountsService],
    );

    const subtitleFilters = React.useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: durationObject.raw.from.valueOf(),
                    to: durationObject.raw.to.valueOf(),
                },
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country).text,
            },
        ],
        [params, durationObject],
    );
    const isPOPMode = useMemo(() => comparedDuration !== "", [comparedDuration]);
    const isCompareMode = useMemo(() => key.split(",").length > 1, [key]);
    const ComponentToRender = useMemo(() => {
        if (!isCompareMode) {
            if (isPOPMode) {
                return COMPONENTS.singlePOP;
            } else if (showBetaBranchData) {
                return COMPONENTS.singleBetaBranch;
            } else {
                return COMPONENTS.single;
            }
        } else {
            // compare mode
            if (isPOPMode) {
                return COMPONENTS.comparePOP;
            } else if (showBetaBranchData) {
                return COMPONENTS.compareBetaBranch;
            } else {
                return COMPONENTS.compare;
            }
        }
    }, [isCompareMode, showBetaBranchData, isPOPMode]);
    useEffect(() => {
        if (isCompareMode) {
            if (displaySegmentsUpsaleBanner) {
                setDisplaySegmentsUpsaleBanner(false);
            }
        } else if (!displaySegmentsUpsaleBanner && !doSegmentsExist && !hasClosedSegmentsUpsaleBanner) {
            setDisplaySegmentsUpsaleBanner(true);
        }
    }, [isCompareMode, displaySegmentsUpsaleBanner, doSegmentsExist, hasClosedSegmentsUpsaleBanner]);

    return ComponentToRender ? (
        <>
            <ComponentToRender
                {...{
                    ...props,
                    apiParams,
                    params,
                    websiteAnalysisApiService: services.websiteAnalysisApiService,
                    isGAPrivate,
                    subtitleFilters,
                }}
            />
            <SegmentsUpsaleBanner
                pageId={EPageIdOption.TRAFFIC_AND_ENGAGEMENT}
                isVisible={displaySegmentsUpsaleBanner}
                closeBanner={closeSegmentsUpsaleBanner}
            />
        </>
    ) : null;
};

const mapStateToProps = (store) => {
    const {
        common: {
            showGAApprovedData,
            showBetaBranchData: { value: showBetaBranchData },
        },
        segmentsModule: { customSegmentsMeta = EMPTY_CUSTOM_SEGMENTS },
    } = store;
    return {
        showGAApprovedData,
        showBetaBranchData,
        customSegmentsMeta,
    };
};

function mapDispatchToProps(dispatch) {
    return {
        betaVsLiveSwitchToggle: (value, isUpdating) => {
            dispatch(betaVsLiveSwitchToggle(value, isUpdating));
        },
        betaVsLiveUpdateCompleted: () => {
            dispatch(betaVsLiveUpdateCompleted());
        },
    };
}

export const WebsiteAnalysisTrafficContainerConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(WebsiteAnalysisTrafficContainer);
